import { Fragment } from "react";
import { Icon } from "@iconify/react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router";
import Avatar from "../components/Avatar";
import CaptableManager from "../components/CaptableManager";
import DownloadsManager from "../components/DownloadsManager";
import FinancialManager from "../components/FinancialManager";
import NotificationBell from "../components/NotificationBell";
import { queryClient } from "../queries/client";
import { useAuthStore } from "../store";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export default function MainLayout() {
  const { clear } = useAuthStore();
  function logout() {
    localStorage.removeItem("accesstoken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("companyId");
    localStorage.removeItem("hissaUrl");
    queryClient.clear();
    clear();
  }

  const user = useAuthStore();
  const role = user.role;
  const userNavigation = [{ name: "Log out", onClick: logout }];
  const navigation =
    role === "Admin" || role === "SuperAdmin"
      ? [
          { name: "Onboarding", href: "/onboarding", current: true },
          { name: "Users", href: "/users", current: true },
          { name: "Hissa Users", href: "/hissaUsers", current: true },
          { name: "Upload", href: "/upload", current: true },
          { name: "ESOP Stats", href: "/esopStats", current: true },
          { name: "Captable Stats", href: "/captableStats", current: true },
          { name: "Global Investor", href: "/globalInvestor", current: true },
          { name: "MCA Orders", href: "/mcaCreatedOrder", current: true },
          { name: "Data Pipeline", href: "/dataPipeline", current: true },
        ]
      : role === "Legal"
        ? [
            { name: "Onboarding", href: "/onboarding", current: true },
            { name: "Upload", href: "/upload", current: true },
          ]
        : role === "FinanceAdmin"
          ? [{ name: "ESOP Stats", href: "/esopStats", current: true }]
          : role === "User"
            ? [
                { name: "MCA Orders", href: "/mcaCreatedOrder", current: true },
                { name: "Data Pipeline", href: "/dataPipeline", current: true },
                { name: "Upload", href: "/upload", current: true },
              ]
            : [{ name: "Onboarding", href: "/onboarding", current: true }];
  const navigate = useNavigate();

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full">
        <body class="h-full">
        ```
      */}
      <div className="min-h-full">
        <Disclosure as="nav" className="border-b border-gray-200 bg-secondary">
          {({ open }) => (
            <>
              <div className="mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 justify-between">
                  <div className="flex">
                    <div className="flex flex-shrink-0 items-center">
                      <img
                        className="h-5 w-auto"
                        src="https://equion-dev.s3.us-west-2.amazonaws.com/logonew.svg"
                        alt="hissa_logo"
                      />
                    </div>
                    <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
                      {navigation.map((item) => {
                        const match = useMatch(`/${item.href.split("/")[1]}/*`);
                        return (
                          <a
                            key={item.name}
                            onClick={() => {
                              navigate(item.href);
                            }}
                            className={classNames(
                              match
                                ? "border-b border-white text-white duration-300"
                                : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300",
                              "inline-flex items-center border-b-2 px-1 pt-1 text-base font-medium cursor-pointer",
                            )}
                            aria-current={item.current ? "page" : undefined}
                          >
                            {item.name}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  <div className="hidden sm:ml-6 sm:flex sm:items-center">
                    <NotificationBell />
                    {/* Profile dropdown */}
                    <Menu as="div" className="relative ml-3">
                      <div>
                        <Menu.Button className="relative flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                          <span className="absolute -inset-1.5" />
                          <span className="sr-only">Open user menu</span>

                          <Avatar name={user.userName ?? ""} />
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {userNavigation.map((item) => (
                            <Menu.Item key={item.name}>
                              {({ active }) => (
                                <a
                                  onClick={item.onClick}
                                  className={classNames(
                                    active ? "bg-gray-100" : "",
                                    "block px-4 py-2 text-sm text-gray-700",
                                  )}
                                >
                                  {item.name}
                                </a>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                  <div className="-mr-2 flex items-center sm:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <span className="absolute -inset-0.5" />
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <Icon
                          icon="octicon:x-16"
                          className="text-blue-700 h-5 cursor-pointer"
                          height={20}
                        />
                      ) : (
                        <Icon
                          icon="ion:menu"
                          className="text-blue-700 h-5 cursor-pointer"
                          height={20}
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="sm:hidden">
                <div className="space-y-1 pb-3 pt-2">
                  {navigation.map((item) => (
                    <Disclosure.Button
                      key={item.name}
                      as="a"
                      href={item.href}
                      className={classNames(
                        item.current
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                          : "border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800",
                        "block border-l-4 py-2 pl-3 pr-4 text-base font-medium",
                      )}
                      aria-current={item.current ? "page" : undefined}
                    >
                      {item.name}
                    </Disclosure.Button>
                  ))}
                </div>
                <div className="border-t border-gray-200 pb-3 pt-4">
                  <div className="flex items-center px-4">
                    <div className="flex-shrink-0">
                      <Avatar name={user.userName ?? ""} />
                    </div>
                    <div className="ml-3">
                      <div className="text-base font-medium text-gray-800">
                        {user.userName}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    {userNavigation.map((item) => (
                      <Disclosure.Button
                        key={item.name}
                        as="a"
                        onClick={item.onClick}
                        className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                      >
                        {item.name}
                      </Disclosure.Button>
                    ))}
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
        <DownloadsManager />
        <CaptableManager />
        <FinancialManager />
        <main className="mx-auto sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </>
  );
}
